import type { TFunction } from 'i18next';
import type { ChartOfAccount } from '@/types/financial-accounts';

/**
 * Account category detection utilities
 * Maps account categories to display types for type-specific views
 */

export type AccountDisplayType =
  | 'revenue'
  | 'expense'
  | 'bank'
  | 'receivable'
  | 'payable'
  | 'asset'
  | 'liability'
  | 'equity';

/**
 * The balance-sheet side of a category name — the one rule (`'asset'` /
 * `'liabilit'` substrings, case-insensitive) that `getAccountDisplayType` uses
 * too. `null` for anything that is neither (equity, revenue, expense, unknown).
 */
export function getBalanceSheetSide(categoryName?: string | null): 'asset' | 'liability' | null {
  const category = categoryName?.toLowerCase() || '';
  if (category.includes('asset')) return 'asset';
  if (category.includes('liabilit')) return 'liability';
  return null;
}

/**
 * Determines the display type for an account based on its category and type names.
 * Used to show type-specific metrics on account detail pages.
 */
export function getAccountDisplayType(
  categoryName?: string,
  typeName?: string
): AccountDisplayType {
  const category = categoryName?.toLowerCase() || '';
  const type = typeName?.toLowerCase() || '';

  // Revenue accounts
  if (category.includes('revenue') || category.includes('income')) {
    return 'revenue';
  }

  // Expense accounts
  if (category.includes('expense') || category.includes('cost')) {
    return 'expense';
  }

  const side = getBalanceSheetSide(category);

  // Asset accounts - check specific types first
  if (side === 'asset') {
    if (type.includes('cash') || type.includes('bank')) {
      return 'bank';
    }
    if (type.includes('receivable') || type.includes('ar')) {
      return 'receivable';
    }
    return 'asset';
  }

  // Liability accounts - check specific types first
  if (side === 'liability') {
    if (type.includes('payable') || type.includes('ap')) {
      return 'payable';
    }
    return 'liability';
  }

  // Equity accounts
  if (category.includes('equity') || category.includes('capital')) {
    return 'equity';
  }

  // Default to asset if unknown
  return 'asset';
}

/**
 * Returns whether an account type should show period comparisons
 * (YTD vs Prior YTD, This Month vs Last Month)
 */
export function shouldShowPeriodComparisons(displayType: AccountDisplayType): boolean {
  return displayType === 'revenue' || displayType === 'expense';
}

/**
 * Returns whether an account type should show aging analysis
 */
export function shouldShowAgingAnalysis(displayType: AccountDisplayType): boolean {
  return displayType === 'receivable' || displayType === 'payable';
}

/**
 * Returns a translated display name for system accounts, or the account_name for non-system accounts.
 */
export function getSystemAccountDisplayName(account: ChartOfAccount, t: TFunction): string {
  if (account.system_account_type) {
    return t(`accounts:systemAccounts.${account.system_account_type}`, account.account_name);
  }
  return account.account_name;
}

/**
 * Maps a line-item form type to the account normal balance that "naturally"
 * adds to that form's running total. AP bills (and the Expense form, which
 * uses type='ap' under the hood) accumulate in the debit direction;
 * AR invoices accumulate in the credit direction.
 */
export const FORM_TYPE_NORMAL_BALANCE = {
  ap: 'debit',
  ar: 'credit',
} as const;

export type LineItemFormType = keyof typeof FORM_TYPE_NORMAL_BALANCE;

/**
 * Balance-sheet categories. A line on these accounts posts on the form's
 * natural side rather than carrying the contra flip. Keyed on the canonical
 * global category names from `financial_account_categories`.
 */
const BALANCE_SHEET_CATEGORIES: ReadonlySet<string> = new Set([
  'Assets',
  'Liabilities',
  'Equity',
]);

/**
 * +1 / -1 sign multiplier for a line-item account on an AP/AR/expense form.
 *
 *   +1 → line posts on the form's natural side (debit on AP/expense, credit on AR)
 *   -1 → line posts on the opposite side (a genuine contra/refund line — e.g. a
 *        Revenue refund line, or a credit-normal Expenses-category contra account)
 *
 * Only income-statement accounts (Expenses/Revenue) carry the contra flip. A
 * balance-sheet account (Assets/Liabilities/Equity) on an expense form is a USE
 * of cash — buying an asset, paying down a loan, an owner draw — and must post on
 * the natural (debit) side, NOT be mistaken for a contra. Defaults to +1 when
 * normal_balance is missing — incomplete lines don't surprise.
 *
 * SQL counterpart: `account_sign_for_form(uuid, text)` (see the
 * fix_account_sign_balance_sheet migration). KEEP IN SYNC.
 *
 * @example
 *   const sign = getAccountSignForForm(account, 'ap');
 *   const total = sign * (item.unit_price * item.quantity);
 */
export function getAccountSignForForm(
  account:
    | { normal_balance?: 'debit' | 'credit' | null; category?: { category_name?: string | null } | null }
    | undefined
    | null,
  formType: LineItemFormType,
): 1 | -1 {
  const nb = account?.normal_balance;
  if (nb !== 'debit' && nb !== 'credit') return 1;
  // Balance-sheet lines post on the form's natural side, so liability/equity
  // pay-downs (and asset purchases) aren't mistaken for contra/refund lines.
  const categoryName = account?.category?.category_name;
  if (categoryName && BALANCE_SHEET_CATEGORIES.has(categoryName)) return 1;
  return nb === FORM_TYPE_NORMAL_BALANCE[formType] ? 1 : -1;
}
